import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useOutletContext, useLocation } from "react-router-dom";
import { useEventData } from "@/hooks/useEventData";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Plus, Search, Archive, Clock, Ban } from "lucide-react";
import { getUnmatchedEvents } from "@/lib/api";
import { EventCard } from "@/components/events/EventCard";
import { EventDialog } from "@/components/events/EventDialog";
import { ReviewTabContent } from "@/components/events/ReviewTabContent";

export default function EventsPage() {
  const { role } = useOutletContext();
  const location = useLocation();
  const highlightEvent = location.state?.highlightEvent;
  const eventTab = location.state?.eventTab;
  const highlightRef = useRef(null);
  const [search, setSearch] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState({ name: "", client_id: "", client_name: "", venue: "", start_date: "", end_date: "", status: "confirmed", revenue: 0, gear_assignments: [], crew_assignments: [], notes: "" });
  const [activeTab, setActiveTab] = useState(eventTab || "upcoming");
  const [mergeSuggestions, setMergeSuggestions] = useState([]);
  const [orphanQBEvents, setOrphanQBEvents] = useState([]);
  const [unmatchedCounts, setUnmatchedCounts] = useState({ qb: 0, gcal: 0 });

  const { events, gear, crew, clients, loading, fetchAll, handleCreate, handleUpdate, handleDelete: deleteEventById } = useEventData(role);

  const unlinkedGcalEvents = useMemo(() =>
    events.filter(e => e.source === "google-calendar" && !e.qb_estimate_id)
      .sort((a, b) => a.start_date.localeCompare(b.start_date)),
    [events]
  );

  const canEdit = role === "owner" || role === "manager";
  const canDelete = role === "owner" || role === "manager";
  const isOwner = role === "owner";
  const isCrew = role === "crew";

  useEffect(() => {
    if (activeTab === "pending" && canEdit) {
      getUnmatchedEvents().then(res => {
        setMergeSuggestions(res.data.suggestions || []);
        setOrphanQBEvents(res.data.orphan_qb_events || []);
        setUnmatchedCounts({ qb: res.data.qb_unlinked_count || 0, gcal: res.data.gcal_unlinked_count || 0 });
      }).catch(() => { /* Non-critical: tab-specific background refresh */ });
    }
  }, [activeTab, events, canEdit]);

  const normalizeGearAssignments = (assignments) => {
    if (!assignments) return [];
    return assignments.map(a => typeof a === 'string' ? { gear_id: a, quantity: 1 } : { gear_id: a.gear_id, quantity: a.quantity || 1 });
  };

  useEffect(() => {
    if (highlightEvent && highlightRef.current && !loading) {
      highlightRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      window.history.replaceState({}, document.title);
    }
  }, [highlightEvent, loading]);

  useEffect(() => {
    if (eventTab) setActiveTab(eventTab);
  }, [eventTab]);

  // Time grouping
  const todayStr = new Date().toISOString().split('T')[0];
  const endOfWeek = (() => { const d = new Date(); d.setDate(d.getDate() + (6 - d.getDay())); return d.toISOString().split('T')[0]; })();
  const endOfNextWeek = (() => { const d = new Date(); d.setDate(d.getDate() + (13 - d.getDay())); return d.toISOString().split('T')[0]; })();

  const groupedEvents = useMemo(() => {
    const pending = events.filter(e => e.status === "pending" && e.end_date >= todayStr);
    const declined = events.filter(e => e.status === "declined");
    const active = events.filter(e => e.status === "confirmed" && e.end_date >= todayStr);
    const archived = events.filter(e => e.status === "completed" || e.status === "cancelled" || (e.end_date < todayStr && e.status !== "declined"));
    active.sort((a, b) => a.start_date.localeCompare(b.start_date));
    pending.sort((a, b) => a.start_date.localeCompare(b.start_date));
    declined.sort((a, b) => b.start_date.localeCompare(a.start_date));
    archived.sort((a, b) => b.start_date.localeCompare(a.start_date));

    const thisWeek = active.filter(e => e.start_date <= endOfWeek);
    const nextWeek = active.filter(e => e.start_date > endOfWeek && e.start_date <= endOfNextWeek);
    const later = active.filter(e => e.start_date > endOfNextWeek);

    return { thisWeek, nextWeek, later, archived, active, pending, declined };
  }, [events, todayStr, endOfWeek, endOfNextWeek]);

  const filteredBySearch = useCallback((list) => list.filter(e =>
    !search || e.name.toLowerCase().includes(search.toLowerCase()) || e.client_name?.toLowerCase().includes(search.toLowerCase())
  ), [search]);

  const openCreate = () => {
    setEditItem(null);
    setForm({ name: "", client_id: "", client_name: "", venue: "", start_date: "", end_date: "", status: "confirmed", revenue: 0, gear_assignments: [], crew_assignments: [], notes: "" });
    setDialogOpen(true);
  };

  const openEdit = useCallback((item) => {
    setEditItem(item);
    setForm({
      name: item.name, client_id: item.client_id, client_name: item.client_name || "",
      venue: item.venue || "", start_date: item.start_date, end_date: item.end_date,
      status: item.status, revenue: item.revenue || 0,
      gear_assignments: normalizeGearAssignments(item.gear_assignments),
      crew_assignments: item.crew_assignments || [], notes: item.notes || ""
    });
    setDialogOpen(true);
  }, []);

  const handleSave = async () => {
    const clientObj = clients.find(c => c.client_id === form.client_id);
    const payload = { ...form, client_name: clientObj?.name || form.client_name };
    try {
      if (editItem) { await handleUpdate(editItem.event_id, payload); }
      else { await handleCreate(payload); }
      setDialogOpen(false);
    } catch (err) { toast.error(err?.response?.data?.detail || "Save failed"); }
  };

  const onDelete = useCallback(async (id) => {
    try { await deleteEventById(id); }
    catch (err) { toast.error(err?.response?.data?.detail || "Delete failed"); }
  }, [deleteEventById]);

  // Compute date-aware crew availability for the event being edited
  const crewWithAvailability = useMemo(() => {
    const eventStart = form.start_date;
    const eventEnd = form.end_date || eventStart;
    if (!eventStart) return crew.map(c => ({ ...c, dateAvail: c.availability || "available" }));

    const overlapping = events.filter(e => {
      if (editItem && e.event_id === editItem.event_id) return false;
      if (e.status === "cancelled" || e.status === "declined") return false;
      const eStart = e.start_date || "";
      const eEnd = e.end_date || eStart;
      return eStart <= eventEnd && eEnd >= eventStart;
    });

    const bookings = {};
    overlapping.forEach(e => {
      (e.crew_assignments || []).forEach(cid => {
        if (!bookings[cid]) bookings[cid] = [];
        bookings[cid].push(e.name);
      });
    });

    return crew.map(c => {
      const staticAvail = c.availability || "available";
      if (staticAvail === "unavailable") return { ...c, dateAvail: "unavailable", bookedFor: [] };
      const bookedFor = bookings[c.crew_id] || [];
      return { ...c, dateAvail: bookedFor.length ? "booked" : "available", bookedFor };
    });
  }, [crew, events, form.start_date, form.end_date, editItem]);

  const toggleGear = (gearId) => {
    setForm(prev => {
      const existing = prev.gear_assignments.find(a => a.gear_id === gearId);
      if (existing) return { ...prev, gear_assignments: prev.gear_assignments.filter(a => a.gear_id !== gearId) };
      return { ...prev, gear_assignments: [...prev.gear_assignments, { gear_id: gearId, quantity: 1 }] };
    });
  };
  const updateGearQty = (gearId, qty) => {
    setForm(prev => ({ ...prev, gear_assignments: prev.gear_assignments.map(a => a.gear_id === gearId ? { ...a, quantity: Math.max(1, parseInt(qty) || 1) } : a) }));
  };
  const toggleCrew = (crewId) => {
    setForm(prev => ({ ...prev, crew_assignments: prev.crew_assignments.includes(crewId) ? prev.crew_assignments.filter(id => id !== crewId) : [...prev.crew_assignments, crewId] }));
  };

  const renderEventCard = useCallback((ev) => {
    const isHighlighted = ev.event_id === highlightEvent;
    return (
      <EventCard
        key={ev.event_id}
        ref={isHighlighted ? highlightRef : null}
        event={ev}
        gear={gear}
        crew={crew}
        canEdit={canEdit}
        canDelete={canDelete}
        isOwner={isOwner}
        isHighlighted={isHighlighted}
        onEdit={openEdit}
        onDelete={onDelete}
        onRefresh={fetchAll}
      />
    );
  }, [highlightEvent, gear, crew, canEdit, canDelete, isOwner, fetchAll, openEdit, onDelete]);

  const renderSection = (title, items, emptyMsg) => {
    const filtered = filteredBySearch(items);
    if (filtered.length === 0 && !search) return null;
    return (
      <div className="space-y-3">
        <h2 className="text-lg font-bold uppercase tracking-tight flex items-center gap-2" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
          {title}
          <Badge variant="secondary" className="text-xs">{filtered.length}</Badge>
        </h2>
        {filtered.length === 0 ? (
          <p className="text-sm text-muted-foreground py-4">{emptyMsg || "No events"}</p>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {filtered.map(ev => renderEventCard(ev))}
          </div>
        )}
      </div>
    );
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div data-testid="events-page" className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-bold uppercase tracking-tight" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>{isCrew ? "My Events" : "Events"}</h1>
          <p className="text-muted-foreground text-sm mt-1">
            {isCrew
              ? `${groupedEvents.active.length} upcoming`
              : `${groupedEvents.active.length} upcoming · ${groupedEvents.pending.length} pending · ${groupedEvents.declined.length} declined · ${groupedEvents.archived.length} completed`}
          </p>
        </div>
        {canEdit && (
          <Button data-testid="add-event-btn" onClick={openCreate} className="bg-primary text-primary-foreground hover:bg-primary/90 text-xs uppercase tracking-wider rounded-sm">
            <Plus className="w-3.5 h-3.5 mr-2" /> New Event
          </Button>
        )}
      </div>

      <div className="flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input data-testid="event-search" placeholder="Search events..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9 bg-secondary/30 border-border h-9 text-sm" />
        </div>
      </div>

      {isCrew ? (
        /* Crew only ever see their upcoming assigned shows — no Review/Archive/
           Declined tabs, which surface company-wide pipeline and financials. */
        <div className="space-y-8">
          {groupedEvents.active.length === 0 && !search && (
            <p className="text-muted-foreground text-center py-10">No upcoming events on your schedule</p>
          )}
          {renderSection("This Week", groupedEvents.thisWeek, "Nothing scheduled this week")}
          {renderSection("Next Week", groupedEvents.nextWeek, "Nothing scheduled next week")}
          {renderSection("Later", groupedEvents.later, "No events scheduled beyond next week")}
        </div>
      ) : (
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-secondary/30 flex-wrap h-auto">
          <TabsTrigger data-testid="tab-upcoming" value="upcoming" className="text-xs uppercase tracking-wider">
            Upcoming ({groupedEvents.active.length})
          </TabsTrigger>
          <TabsTrigger data-testid="tab-pending" value="pending" className="text-xs uppercase tracking-wider">
            <Clock className="w-3 h-3 mr-1.5" />Review ({groupedEvents.pending.length + unmatchedCounts.qb})
          </TabsTrigger>
          <TabsTrigger data-testid="tab-archive" value="archive" className="text-xs uppercase tracking-wider">
            <Archive className="w-3 h-3 mr-1.5" />Archive ({groupedEvents.archived.length})
          </TabsTrigger>
          <TabsTrigger data-testid="tab-declined" value="declined" className="text-xs uppercase tracking-wider">
            <Ban className="w-3 h-3 mr-1.5" />Declined ({groupedEvents.declined.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="upcoming" className="space-y-8 mt-6">
          {groupedEvents.active.length === 0 && !search && (
            <p className="text-muted-foreground text-center py-10">No upcoming events</p>
          )}
          {renderSection("This Week", groupedEvents.thisWeek, "Nothing scheduled this week")}
          {renderSection("Next Week", groupedEvents.nextWeek, "Nothing scheduled next week")}
          {renderSection("Later", groupedEvents.later, "No events scheduled beyond next week")}
        </TabsContent>

        <TabsContent value="pending">
          <ReviewTabContent
            mergeSuggestions={mergeSuggestions}
            setMergeSuggestions={setMergeSuggestions}
            orphanQBEvents={orphanQBEvents}
            unmatchedCounts={unmatchedCounts}
            pendingEvents={groupedEvents.pending}
            unlinkedGcalEvents={unlinkedGcalEvents}
            canEdit={canEdit}
            fetchAll={fetchAll}
            filteredBySearch={filteredBySearch}
            renderEventCard={renderEventCard}
          />
        </TabsContent>

        <TabsContent value="archive" className="mt-6">
          {groupedEvents.archived.length === 0 ? (
            <p className="text-muted-foreground text-center py-10">No archived events</p>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {filteredBySearch(groupedEvents.archived).map(ev => renderEventCard(ev))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="declined" className="mt-6">
          {groupedEvents.declined.length === 0 ? (
            <div className="text-center py-10">
              <Ban className="w-8 h-8 text-muted-foreground/30 mx-auto mb-3" />
              <p className="text-muted-foreground">No declined estimates</p>
              <p className="text-xs text-muted-foreground/60 mt-1">Estimates marked as Closed or Rejected in QuickBooks will appear here.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {filteredBySearch(groupedEvents.declined).map(ev => renderEventCard(ev))}
            </div>
          )}
        </TabsContent>
      </Tabs>
      )}

      <EventDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        editItem={editItem}
        form={form}
        setForm={setForm}
        clients={clients}
        gear={gear}
        crewWithAvailability={crewWithAvailability}
        isOwner={isOwner}
        onSave={handleSave}
        onToggleGear={toggleGear}
        onUpdateGearQty={updateGearQty}
        onToggleCrew={toggleCrew}
      />
    </div>
  );
}
